import React, { useState, useEffect } from 'react';
import {
  TextField, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Box, Button, Dialog, DialogTitle, DialogContent,
  TablePagination
} from '@mui/material';
import { supabase } from '../../lib/supabaseClient';
import { format } from 'date-fns';

const CastAnalytics = () => {
  const [casts, setCasts] = useState([]);
  const [filteredCasts, setFilteredCasts] = useState([]);
  const [searchName, setSearchName] = useState('');
  const [searchMobile, setSearchMobile] = useState('');
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [isProjectsDialogOpen, setIsProjectsDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch all data in a single query using foreign key relationships
      const { data: castsData, error: castsError } = await supabase
        .from('principal_artist')
        .select(`
          cast_id,
          name,
          phone,
          role,
          created_at,
          principal_artist_project_mapping!inner (
            project:project (
              project_id,
              project_name,
              created_at
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (castsError) throw castsError;

      // Group casts by name and phone
      const groupedCasts = castsData.reduce((acc, cast) => {
        const key = `${cast.name}-${cast.phone}`;
        if (!acc[key]) {
          acc[key] = {
            name: cast.name,
            phone: cast.phone,
            created_at: cast.created_at,
            roles: [cast.role],
            projects: cast.principal_artist_project_mapping.map(m => m.project)
          };
        } else {
          // Add new role if it doesn't exist
          if (!acc[key].roles.includes(cast.role)) {
            acc[key].roles.push(cast.role);
          }
          // Add new projects
          const newProjects = cast.principal_artist_project_mapping.map(m => m.project);
          acc[key].projects.push(...newProjects);
          // Keep earliest creation date
          if (new Date(cast.created_at) < new Date(acc[key].created_at)) {
            acc[key].created_at = cast.created_at;
          }
        }
        return acc;
      }, {});

      // Convert to array and remove duplicate projects
      const combinedCasts = Object.values(groupedCasts).map(cast => ({
        ...cast,
        projects: [...new Map(cast.projects.map(p => [p.project_id, {
          ...p,
          characterName: cast.roles.join(', ')
        }])).values()].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
      }));

      // Sort by created_at in descending order
      const sortedCasts = combinedCasts.map(cast => ({
        ...cast,
        projectCount: cast.projects.length
      })).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      setCasts(sortedCasts);
      setFilteredCasts(sortedCasts);

    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleSearch = () => {
    let filtered = casts;

    if (searchName) {
      filtered = filtered.filter(cast =>
        cast.name?.toLowerCase().includes(searchName.toLowerCase())
      );
    }

    if (searchMobile) {
      filtered = filtered.filter(cast =>
        cast.phone?.includes(searchMobile)
      );
    }

    setFilteredCasts(filtered);
    setPage(0);
  };

  const handleProjectsClick = (projects) => {
    setSelectedProjects(projects);
    setIsProjectsDialogOpen(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginatedCasts = filteredCasts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div className="cast-analytics">
      <h2>Cast Analytics</h2>

      {/* Search Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search by Name"
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
        />
        <TextField
          label="Search by Mobile"
          value={searchMobile}
          onChange={(e) => setSearchMobile(e.target.value)}
        />
        <Button variant="contained" onClick={handleSearch}>
          Search
        </Button>
      </Box>

      {/* Cast Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>S. No.</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Mobile Number</TableCell>
              <TableCell>Created On</TableCell>
              <TableCell>No. of Projects</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedCasts.map((cast, index) => (
              <TableRow key={cast.cast_id}>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell>{cast.name}</TableCell>
                <TableCell>{cast.phone}</TableCell>
                <TableCell>
                  {format(new Date(cast.created_at), 'dd/MM/yyyy')}
                </TableCell>
                <TableCell>
                  <Button
                    onClick={() => handleProjectsClick(cast.projects)}
                    variant="text"
                    color="primary"
                    disabled={cast.projectCount === 0}
                  >
                    {cast.projectCount}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredCasts.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[20]}
        />
      </TableContainer>

      {/* Projects Dialog */}
      <Dialog
        open={isProjectsDialogOpen}
        onClose={() => setIsProjectsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Projects</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>S. No.</TableCell>
                  <TableCell>Project Name</TableCell>
                  <TableCell>Character Name</TableCell>
                  <TableCell>Creation Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedProjects.map((project, index) => (
                  <TableRow key={project.project_id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{project.project_name}</TableCell>
                    <TableCell>{project.characterName}</TableCell>
                    <TableCell>
                      {format(new Date(project.created_at), 'dd/MM/yyyy')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CastAnalytics; 